import React, { FC, useCallback, useContext, useRef, useState, Fragment, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDeterminedDirection } from "../../selectors/useDeterminedDirection";
import { useMergedOptions } from "../../selectors/useMergedOptions";
import DruidWebchatContext, { DruidWebchatContextType, WebChatHeaderOptions } from "../../WebChatContext";
import { DruidOptionsContainer } from "./DruidOptionsContainer";
import { DruidMinimizeSelector } from "./DruidMinimizeSelector";

import { DruidReloadSelector } from "./DruidReloadSelector";
import { DruidResetSelector } from "./DruidResetSelector";

import IconButton from 'botframework-webchat-component/lib/SendBox/IconButton';

import * as bsIcons from 'react-icons/bs';
import * as faIcons from 'react-icons/fa';

import classNames from "classnames";
import Button from "react-bootstrap/Button";
import Collapse from "react-bootstrap/Collapse";
import { useWebChatRefsContext } from "../../hooks/useWebChatRefsContext";
import { DruidCustomButton } from "./DruidCustomButton";
import { useDraggableEffect } from "../../hooks/useDraggableEffect";



export const DruidHeader = ({ children }: { children?: any }) => {
  const [druidWebChatState, druidWebChatDispatch, , druidStyleToEmotionObject] = useContext<DruidWebchatContextType>(DruidWebchatContext);
  const { setHeaderRef, setHeaderWasDragged } = useWebChatRefsContext();

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerRef = useRef(null);

  const determinedDir = useDeterminedDirection();
  const { VoiceInputSelector = null, DruidLanguageSelector = null } = children || {};

  const { disabled, isWidget,
    UI: { v2_chatTitle_icon = "", v2_minimize_icon = "", v2_switch_icon = "", restylingsEnabled, fullScreenMode, draggableEnabled, chatTitle, headerTextColor, headerFontSize, rootFontSize, lineHeight, headerOuterPadding, headerButtonColorOnFocus, headerBottomBorderRadius, headerBorderRadius, headerTextColorOnHover, headerBackground } = {},
    styleOptions: { paddingRegular, accent, subtle, sendBoxButtonColorOnHover, sendBoxButtonColorOnDisabled, backgroundColor, botAvatarImage, sendBoxButtonColorOnFocus } = {},
  } = useMergedOptions();
  const { showHeader, showChatTitle, showChangeSideButton, showReload, showCloseButton, showLanguageSelector, showAvatar, showIcon, showVoiceInputSelector, showReset } = druidWebChatState.headerOptions;
  const {  INITIALIZATION_STATUS } = druidWebChatState;
  const { isVoiceChannel, speechToken } = druidWebChatState.speechOptions;

  useEffect(() => {
    setHeaderRef(headerRef.current || undefined);
  }, [headerRef, setHeaderRef]);

  
  const [onDragStart, wasDragged] = useDraggableEffect(draggableEnabled, headerRef, "druid__minimizable__web-chat__chat-box");
  useEffect(() => {
    setHeaderWasDragged(wasDragged);
  }, [wasDragged, setHeaderWasDragged]);

  const TitleIcon = bsIcons[v2_chatTitle_icon] || faIcons[v2_chatTitle_icon];
  const ROOT_STYLE = {
    '&.druid__webchat__header': {
      color: headerTextColor,
      backgroundColor: INITIALIZATION_STATUS === 'success' ? backgroundColor: 'transparent',
      cursor: 'pointer',
      padding: restylingsEnabled && 5,
      '& .druid__webchat__header-container': {
        
        backgroundColor: accent,
        background: headerBackground,
        color: headerTextColor,
        height: 'auto',
        padding: headerOuterPadding || paddingRegular,
        display: 'flex',
        columnGap: paddingRegular! / 2,
        borderBottomRightRadius: headerBottomBorderRadius,
        borderBottomLeftRadius: headerBottomBorderRadius,
        borderRadius: restylingsEnabled && headerBorderRadius,
        '& .filler': {
          flex: '0 10000 100%'
        },
        '.druid__webchat__header-title':{
          margin: 'auto',
          alignSelf: 'center',
          fontSize: headerFontSize || rootFontSize,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        },
        '.druid__webchat__header-avatar': {
          height: `calc(${headerFontSize} * ${lineHeight !== 'normal'? lineHeight : 1.2})`,
          transform: 'scale(1.3)'
        },
        '.druid__webchat__header-button': {
          color: headerTextColor,
          backgroundColor: 'Transparent',
          border: 0,
          outline: 0,
          padding: 0,
          width: 'auto',
          margin: 'auto',
          display: 'flex',
          '& > svg': {
            fill: headerTextColor,
            // backgroundColor: 'Transparent'
          },
          '&.webchat__icon-button .webchat__icon-button__shade': {
            display: 'none'
          },
          
          '&[aria-disabled]': { // on button disabled
            color: sendBoxButtonColorOnDisabled,
            '& > svg': {
              fill: sendBoxButtonColorOnDisabled
            }
          },
          '&:not([aria-disabled])': {
            '&:hover' :{
              color: headerTextColorOnHover || sendBoxButtonColorOnHover,
              '& > svg': {
                fill: headerTextColorOnHover || sendBoxButtonColorOnHover
              }
            },
            '&:focus': {
              color: headerButtonColorOnFocus || sendBoxButtonColorOnFocus,
              '& > svg': {
                fill: headerButtonColorOnFocus || sendBoxButtonColorOnFocus
              }
            }
          },
          '&.druid__webchat__header-fadeIn':{
            animationName: 'fadeIn',
            animationDuration: '1s'
          },
          '&.druid__webchat__header-switch': {
           
          },
          '&.druid__webchat__header-minimize': { // this is not <IconButton> type
            '&:hover > svg': {
              fill: headerTextColorOnHover || sendBoxButtonColorOnHover
            },
            '&:focus > svg': {
              fill: headerButtonColorOnFocus || sendBoxButtonColorOnFocus
            }
          },
          '&.druid__webchat__header-microphone': {
            transform: 'scale(1.2)',
            
          },
          '&.druid__webchat__header-keyboard': {
            transform: 'scale(1.2)',
            
          },
          '&.druid__webchat__header-reload': {
            transform: 'scale(1.2)',
            
          },
          '&.druid__webchat__header-reset': {
           
          },
          '&.druid__webchat__header-toggle-menu': {
          }
        },
       
      }
    }
  };


  const rootClassName = druidStyleToEmotionObject(ROOT_STYLE) + '';
  return (
    showHeader ? 
    <div ref={headerRef} dir={determinedDir} className={classNames('druid__webchat__header', rootClassName + '')} 
    onDragStart={onDragStart} draggable={!!draggableEnabled}
    onClick={(e) => { if(isWidget && !fullScreenMode && (e.target instanceof HTMLElement) && (/*e.target.nodeName === "HEADER"  Header was removed  || */ e.target.nodeName == "DIV")) { druidWebChatDispatch({ type: 'CLOSE_WEBCHAT' }); }}}>
      <div className="druid__webchat__header-container">
        {
           showAvatar && !TitleIcon && ((botAvatarImage && <img className="druid__webchat__header-avatar" src={botAvatarImage} alt={chatTitle}/>))
        }
        {
          showIcon && TitleIcon && <div className="druid__webchat__header-icon m-auto"><TitleIcon size="24" /></div>
        }
        
        {showChatTitle && <div className="druid__webchat__header-title" role="heading" aria-level={1} title={chatTitle}>
          {chatTitle}
        </div>}
        { INITIALIZATION_STATUS === 'success' &&<DruidCustomButton>{children}</DruidCustomButton>}
        <div className="filler" />
         
        { INITIALIZATION_STATUS === 'success' &&<DruidOptionsContainer>{children}</DruidOptionsContainer>}
        {
          showLanguageSelector && DruidLanguageSelector
        }    
        {
          // isVoiceChannel && speechToken. speechToken was added because of isVoiceChannel: true bug from backend.
          isVoiceChannel && speechToken && showVoiceInputSelector  && VoiceInputSelector // this is DruidIsVoiceInputSelector. should refactor.
        }

        {
          showCloseButton && <DruidMinimizeSelector/>
        }
      </div>
    </div>
    :
    <>
    </>
  );
}